import React, { useMemo, useState } from 'react';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { useStacklineTheme } from '@stackline/ui';
import { useAppSelector } from 'src/utils/Hooks';
import BusinessUnitTable from './BusinessUnitTable';
import Box from '@mui/material/Box';
import { EMPTY_BUSINESS_UNIT_NAME } from './utils';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BusinessUnitsCreationModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/BusinessUnitsCreation/BusinessUnitsCreationModal';
import ArrowRight from '!svg-react-loader!../../ProductGrowthMatrixCard/assets/arrowRight.svg'; // eslint-disable-line
import { NoDataPlaceholderWithChildren } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/NoDataPlaceholder';
import { CreateNewSegmentLink } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsViewTab';

const NoDataPlaceholder = ({
  setBusinessUnitCreationModalOpen
}: {
  setBusinessUnitCreationModalOpen: (open: boolean) => void;
}) => {
  const { mySegments, teamSegments } = useAppSelector((state) => state.segments);

  return (
    <Box sx={{ margin: '80px auto 0px' }}>
      <NoDataPlaceholderWithChildren
        title="Add business unit"
        subtitle="There are currently no business units to display. All of your business units will be available on this page."
      >
        {mySegments.length > 0 || teamSegments.length > 0 ? (
          <Text
            onClick={() => {
              setBusinessUnitCreationModalOpen(true);
            }}
            sx={{
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Create a business unit
            <ArrowRight
              style={{
                marginLeft: '4px',
                position: 'relative',
                top: '2px'
              }}
            />
          </Text>
        ) : (
          <CreateNewSegmentLink />
        )}
      </NoDataPlaceholderWithChildren>
    </Box>
  );
};

/**
 * Business units tab on the segments page
 */
const BusinessUnitsTab = () => {
  const [value, setValue] = useState('');
  const businessUnits = useAppSelector((state) => state.segments.businessUnits);
  const [businessUnitCreationModalOpen, setBusinessUnitCreationModalOpen] = useState(false);
  const theme = useStacklineTheme();

  const filteredBusinessUnits = useMemo(() => {
    return businessUnits.filter((unit) =>
      (unit.displayName || EMPTY_BUSINESS_UNIT_NAME).toLowerCase().includes(value.toLowerCase())
    );
  }, [businessUnits, value]);

  return (
    <Flex flexDirection="column">
      <SearchTextField
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Search business units"
      />
      {filteredBusinessUnits.length < 1 && (
        <NoDataPlaceholder setBusinessUnitCreationModalOpen={setBusinessUnitCreationModalOpen} />
      )}
      {filteredBusinessUnits.length > 0 && (
        <Box marginTop={theme.spacing.lg}>
          <Flex flexDirection="column" gap={104}>
            {[...filteredBusinessUnits]
              .sort((a, b) => b.children.length - a.children.length) // Show units with most segments first
              .map((businessUnit) => (
                <BusinessUnitTable key={businessUnit.id} businessUnit={businessUnit} />
              ))}
          </Flex>
        </Box>
      )}
      <BusinessUnitsCreationModal
        handleClose={() => {
          setBusinessUnitCreationModalOpen(false);
        }}
        open={businessUnitCreationModalOpen}
      />
    </Flex>
  );
};

export default BusinessUnitsTab;
